import { useContext, useState, useEffect, useRef } from 'react'
import { useFormikContext, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { addDays, addMinutes, clamp, roundToNearestMinutes, setHours, setMinutes, setSeconds } from 'date-fns'
import { motion, AnimatePresence } from "framer-motion"
import { InputField, CheckboxField, FormContent, FormHeader, ButtonDrawer, BookingSummary, FormError, DatePicker, Notifications, BasicModal, NumberStepper } from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import EditFieldsExpirationWarning from '../EditFieldsExpirationWarning'
import { wrapWithValidation } from '~/utils'
import { showContentModal } from '~/lib/modal'
import { formatTime } from '~/utils/format'
import { hasInformationTag, hasAvailability, isBookingAvailable, isBookingAvailableDelayed, isBookingAvailableAsRequested, isEditMode, getLatestPrebookingDateTime } from '~/utils/data'
import { useDidMountEffect } from '~/utils/hooks'
import classnames from 'classnames/bind'
import * as common from '~/config/common.module.css'
import * as styles from './index.module.css'
import * as MotionElement from '~/components/ui/MotionElement'
import { useTranslation } from 'react-i18next'
import i18next from '~/i18n'

const cx = classnames.bind(common)

const PassengerRequiredAlert = ({ onClose }) => {
  const { t } = useTranslation()
  return (
    <div>
      <div className="flex gap-1 items-center px-4 pt-4">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0" aria-hidden="true">
          <g clipPath="url(#pra-clip)">
            <path fillRule="evenodd" clipRule="evenodd" d="M8 14.4333C11.553 14.4333 14.4333 11.553 14.4333 8C14.4333 4.44697 11.553 1.56667 8 1.56667C4.44697 1.56667 1.56667 4.44697 1.56667 8C1.56667 11.553 4.44697 14.4333 8 14.4333ZM8 15.5C12.1421 15.5 15.5 12.1421 15.5 8C15.5 3.85786 12.1421 0.5 8 0.5C3.85786 0.5 0.5 3.85786 0.5 8C0.5 12.1421 3.85786 15.5 8 15.5Z" fill="#17181A"/>
            <rect x="7.2" y="4" width="1.6" height="1.6" rx="0.8" fill="#17181A"/>
            <rect x="7.467" y="6.667" width="1.067" height="5.333" rx="0.213" fill="#17181A"/>
          </g>
          <defs>
            <clipPath id="pra-clip"><rect width="16" height="16" fill="white"/></clipPath>
          </defs>
        </svg>
        <p className="text-headline font-semibold uppercase text-on-surface">
          {t('modal-passenger-required-title')}
        </p>
      </div>
      <div className="px-4 pt-3 pb-4 text-on-surface-variant text-body-sm">
        <p>{t('modal-passenger-required-body')}</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="w-full py-3 bg-on-surface text-on-primary text-btn font-semibold uppercase tracking-[0.68px] leading-5"
      >
        OK
      </button>
    </div>
  )
}

export const getMinDate = (prebookTripRules) => {
  const earliest = prebookTripRules?.earliestPrebookingDateTime
  if (earliest) {
    const d = addMinutes(new Date(earliest), 5)
    // Floor to nearest 5 minutes; use explicit constructor to zero milliseconds so
    // repeated API calls with slightly different ms values don't cause comparison drift
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), Math.floor(d.getMinutes() / 5) * 5, 0, 0)
  }
  return addMinutes(setSeconds(roundToNearestMinutes(new Date(), { nearestTo: 5 }), 0), 40)
}
export const getMaxDate = (values) => {
  if (values) {
    return getLatestPrebookingDateTime(values)
  }
  // Fallback to 28 days ahead if values not available (27 days + today)
  return addDays(setHours(setMinutes(setSeconds(new Date(), 0), 59), 23), 27)
}

const SelectTime = ({fullscreen, currentView}) => {
  const { t } = useTranslation()
  const { prev, next } = useContext(StepperContext)
  const wrappedNext = wrapWithValidation(next, useFormikContext())

  const { values, setFieldValue } = useFormikContext()
  const inEditMode = isEditMode(values)
  const departureIsAirport = hasInformationTag(values, 'FLIGHT_NBR');
  const requiresPassengerCount = hasInformationTag(values, 'TRAVELER_COUNT_REQUIRED');

  // Max passengers = highest maxTravelers across available service types
  const availabilityKey = values.pickupTime === '' ? 'direct' : 'prebook'
  const availabilities = values.availability?.[availabilityKey]?.availabilities || []
  const maxPassengers = availabilities.reduce((max, a) => {
    if (a.availableAt === 'NOT_AVAILABLE' || !a.maxTravelers) return max
    return Math.max(max, a.maxTravelers)
  }, 0) || 50

  // Check if specific fields are editable in edit mode
  const isTimeEditable = inEditMode ? values.editableFields?.includes('pickUpTimeStamp') : true

  // Only check availability if time is editable or not in edit mode
  // If time is not editable, we're keeping the original booking time regardless of current availability
  const isDirectBookDisabled = hasAvailability(values, 'direct') ? !isBookingAvailable(values, 'direct') : false
  const directTripRules = values.availability?.direct?.tripRules ?? values.availability?.prebook?.tripRules
  const goNowNeedsDestination = departureIsAirport || directTripRules?.directBookingRequiresDestination === true
  const noDirectBooking = hasInformationTag(values, 'NO_DIRECT_BOOK')
  const isGoNowDisabled = noDirectBooking || isDirectBookDisabled || (goNowNeedsDestination && !values.destination?.address)
  const isBookingDelayed = (isTimeEditable && hasAvailability(values, 'prebook')) ? isBookingAvailableDelayed(values, 'prebook') : false
  const isBookingDisabled = (isTimeEditable && hasAvailability(values, 'prebook')) ? !isBookingAvailableAsRequested(values, 'prebook') : false

  const prebookTripRules = values.availability?.prebook?.tripRules
  const minDate = getMinDate(prebookTripRules)
  const maxDate = getMaxDate(values)

  // If the selected time is before the UI minimum (earliestPrebookingDateTime + 5 min buffer),
  // keep the button enabled so goNext can show the correction modal. Without this,
  // isBookingDisabled would disable the button and the click would never reach goNext.
  // Only apply when we actually have API-derived data (guard on earliestPrebookingDateTime).
  const isTimeOverdue = !inEditMode
    && !!prebookTripRules?.earliestPrebookingDateTime
    && !!values.pickupTime
    && new Date(values.pickupTime) < minDate
  const prebookRequiresDestination = prebookTripRules?.preBookingRequiresDestination === true
  // isBookingDelayed keeps the button live for the same reason isTimeOverdue does. The
  // modal that offers the next available time is raised from useDidMountEffect, and only
  // when isNextClicked — which nothing sets but goNext, which needs an enabled button. Its
  // condition is `isBookingDisabled && isBookingDelayed`, so disabling on isBookingDisabled
  // alone makes that branch unreachable by clicking and the user has no way forward at all.
  // It appeared to work only while isTimeOverdue happened to be true as well; any re-render
  // that corrected pickupTime up to minDate dropped that and left the button greyed out.
  const isContinueDisabled = (isTimeOverdue || isBookingDelayed) ? false : isBookingDisabled

  const [isNextClicked, setIsNextClicked] = useState(false)
  // Blocks useDidMountEffect from acting on re-fetch results while the overdue-correction
  // modal is visible. A ref (not state) so it's synchronously visible across renders.
  const isShowingCorrectionModal = useRef(false)
  const isFlightNumberEditable = inEditMode ? values.editableFields?.includes('originLocation.flightInfo.flightNumber') : false
  const isCheckedBaggageEditable = inEditMode ? values.editableFields?.includes('originLocation.flightInfo.checkedInBaggage') : false
  const showFlightFields = departureIsAirport && (isFlightNumberEditable || isCheckedBaggageEditable || !inEditMode)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState()
  const [modalMessage, setModalMessage] = useState()
  const [modalButtons, setModalButtons] = useState()

  // Check if the time has been changed from the original
  const originalTime = values?.originalPickupTime
  const currentTime = values?.pickupTime
  const timeHasChanged = originalTime && currentTime &&
    new Date(originalTime).getTime() !== new Date(currentTime).getTime()

  // In edit mode with unchanged time, don't clamp or enforce min/max
  // Once the user changes the time, apply all constraints
  const shouldEnforceConstraints = !inEditMode || timeHasChanged

  const clampDate = shouldEnforceConstraints
    ? (date => clamp(date, { start: minDate, end: maxDate }))
    : (date => date) // Identity function - don't modify the date

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const showInvalidTimeModal = () => {
    setIsModalOpen(true)
    setModalTitle(t('title-invalid-time'))
    setModalMessage(t('message-invalid-time'))
    setModalButtons([{ func: closeModal, title: 'Ok' }])
  }

  const goNow = () => {
    setFieldValue('pickupTime', '', false)
    setFieldValue('flightNumber', '', false)
    setFieldValue('checkedInBaggage', false, false)
    next()
  }

  // effectiveTime lets callers pass a just-set time directly, bypassing the stale
  // Formik values.pickupTime (setFieldValue is async — the new value isn't in values yet)
  const goNext = (effectiveTime) => {
    // When passed as an onClick handler, React provides a SyntheticEvent as the first arg.
    // Ignore it — only accept an actual Date or ISO string as an override.
    const isValidTime = effectiveTime instanceof Date || (typeof effectiveTime === 'string' && effectiveTime !== '')
    const pickupTime = isValidTime ? effectiveTime : values.pickupTime

    if (pickupTime === '') {
      setIsNextClicked(true)
      setFieldValue('pickupTime', minDate, false)
      return
    }

    if (isValidTime) {
      // Called from the correction modal with an explicit accepted time — skip the re-fetch
      // and proceed directly. The time has already been validated against the current minDate.
      if (requiresPassengerCount && values.passengerCount === 0) {
        showContentModal((close) => <PassengerRequiredAlert onClose={close} />)
      } else {
        wrappedNext()
      }
      return
    }

    // Normal Next click: trigger a fresh service-availability fetch so the overdue check in
    // useDidMountEffect runs against the latest earliestPrebookingDateTime, not cached data.
    // The Fetcher re-fetches whenever pickupTime changes — a new Date object with the same
    // millisecond value is a different reference and triggers it.
    if (values.availabilityIsLoading) {
      setIsNextClicked(true)
      return
    }
    setIsNextClicked(true)
    const pt = pickupTime instanceof Date ? pickupTime : new Date(pickupTime)
    setFieldValue('pickupTime', new Date(pt.getTime()), false)
  }

  const setPropsedTime = () => {
    const service = values.availability.prebook.availabilities.find(item => item.availableAt === 'DELAYED')
    const time = new Date(service.pickupTime)
    setFieldValue('pickupTime', time, false)
    goNext(time)
  }

  useEffect(() => {
    if (inEditMode) {
      // Store the original pickup time for comparison in validation
      if (values.pickupTime) {
        setFieldValue('originalPickupTime', values.pickupTime, false);
      }
      return;
    }
    // In create mode, set default time if empty
    if (values.pickupTime === '') {
      setFieldValue('pickupTime', minDate, false);
    }
  }, []);

  // Prebook availability (and its earliestPrebookingDateTime) arrives after the initial
  // render — the Fetcher only fetches it once pickupTime is set to something non-empty.
  //
  // First entry (pickupTimeInitialized=false): snap to API minimum + 5 min buffer so the
  // user sees a sensible default. Mark initialized in Formik so this only runs once per
  // booking session — the flag survives SelectTime unmounting (navigating away and back).
  //
  // Re-entry or re-fetch (pickupTimeInitialized=true): only correct if the existing time
  // has become strictly before the API hard minimum (no buffer). This preserves a time the
  // user already confirmed even if it falls within the 5-min buffer zone.
  useEffect(() => {
    const earliest = prebookTripRules?.earliestPrebookingDateTime
    if (inEditMode) return
    if (!earliest) return
    const newMinDate = getMinDate(prebookTripRules)
    const currentPickup = values.pickupTime ? new Date(values.pickupTime) : null
    if (!values.pickupTimeInitialized) {
      setFieldValue('pickupTimeInitialized', true, false)
      // Always snap to the API-derived minimum on first entry so the user sees a
      // meaningful default — not the generic now+40min fallback which is likely ahead of it.
      setFieldValue('pickupTime', newMinDate, false)
    } else if (currentPickup && currentPickup < new Date(earliest)) {
      // Time is genuinely before the API hard minimum — correct it
      setFieldValue('pickupTime', newMinDate, false)
    }
  }, [prebookTripRules?.earliestPrebookingDateTime])

  // Auto-navigate to next view when flight fields become non-editable due to expiration
  useEffect(() => {
    // Only auto-navigate if:
    // 1. We're in edit mode
    // 2. This is an airport departure (has flight fields)
    // 3. Flight fields are now hidden (both flight number and baggage fields are not editable)
    // 4. We're currently on this view
    if (inEditMode && departureIsAirport && !showFlightFields && currentView) {
      console.log('[SelectTime] Flight fields expired - auto-navigating to next view')
      // Small delay to allow state updates to complete
      const timer = setTimeout(() => {
        next()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [showFlightFields, inEditMode, departureIsAirport, currentView, next])

  // In edit mode, don't allow back navigation to SelectLocations
  const buttons = inEditMode
    ? [
        { func: goNow, title: t('button-go-now'), disabled: true }, // Always disabled when editing - can't change to "go now"
        {
          func: goNext,
          title: t('button-continue'),
          disabled: isBookingDisabled,
          isLoading: (isNextClicked || isBookingDisabled) && values.availabilityIsLoading
        },
      ]
    : [
        { func: () => { setFieldValue('pickupTime', '', false); prev() }, isBack: true },
        { func: goNow, title: t('button-go-now'), disabled: isGoNowDisabled },
        {
          func: goNext,
          title: t('button-continue'),
          disabled: isContinueDisabled,
          isLoading: (isNextClicked || isContinueDisabled) && values.availabilityIsLoading
        },
      ]

  useDidMountEffect(() => {
    console.log('[useDidMountEffect] isShowingCorrectionModal:', isShowingCorrectionModal.current, 'isNextClicked:', isNextClicked, 'availabilityIsLoading:', values.availabilityIsLoading, 'isBookingDisabled:', isBookingDisabled, 'isBookingDelayed:', isBookingDelayed, 'pickupTime:', values.pickupTime, 'minDate:', minDate)
    // While any correction modal is visible, block all re-fetch results from acting.
    if (isShowingCorrectionModal.current) return

    // Edit mode: skip availability checks when time is unchanged; only navigate on Next.
    if (inEditMode && !timeHasChanged) {
      if (isNextClicked) wrappedNext()
      return
    }

    // Only act when the user explicitly clicked Next. Background re-fetches triggered by
    // the snap effect run with isNextClicked=false and must be ignored — otherwise they
    // would show modals or navigate without user intent.
    if (!isNextClicked) return

    if (hasAvailability(values, 'prebook') && !values.availabilityIsLoading) {
      if (isBookingDisabled && !isBookingDelayed) {
        setIsNextClicked(false)
        setIsModalOpen(true)
        setModalTitle(t('title-fully-booked'))
        setModalMessage(t('message-select-other-time'))
        setModalButtons([
          { func: closeModal, title: 'Ok' },
        ])
      }
      else if (isBookingDisabled && isBookingDelayed) {
        setIsNextClicked(false)
        const service = values.availability.prebook.availabilities.find(item => item.availableAt === 'DELAYED')
        const time = formatTime(new Date(service.pickupTime))
        setIsModalOpen(true)
        setModalTitle(t('title-fully-booked'))
        setModalMessage(t('message-select-next-available-time', { time }))
        setModalButtons([
          { func: closeModal, title: t('button-select-other-time') },
          { func: setPropsedTime, title: t('button-select-available-time') }
        ])
      }
      else {
        if (prebookTripRules?.earliestPrebookingDateTime && new Date(values.pickupTime) < minDate) {
          const correctedTime = minDate
          isShowingCorrectionModal.current = true
          setIsNextClicked(false)
          setFieldValue('pickupTime', correctedTime, false)
          const timeStr = formatTime(correctedTime)
          setIsModalOpen(true)
          setModalTitle(t('title-fully-booked'))
          setModalMessage(t('message-select-next-available-time', { time: timeStr }))
          setModalButtons([
            { func: () => { isShowingCorrectionModal.current = false; closeModal() }, title: t('button-select-other-time') },
            { func: () => { isShowingCorrectionModal.current = false; closeModal(); goNext(correctedTime) }, title: t('button-select-available-time') },
          ])
        } else {
          if (requiresPassengerCount && values.passengerCount === 0) {
            setIsNextClicked(false)
            showContentModal((close) => <PassengerRequiredAlert onClose={close} />)
          } else {
            wrappedNext()
          }
        }
      }
    }
  }, [values.availability, values.availabilityIsLoading])

  return (
      <div className={cx(common.widgetWrapper, {fullscreen:fullscreen})}>
      <BasicModal 
        title={modalTitle}
        message={modalMessage}
        buttons={modalButtons} 
        onClose={closeModal} 
        isOpen={isModalOpen} 
        absolute 
      />        
        <FormContent>
          <FormHeader onlyFullscreen>{inEditMode ? t('edit-your-trip') : t('your-trip')}</FormHeader>
          <FormHeader onlyMobile>{departureIsAirport ? t('header-when-does-your-flight-land') : t('header-when-do-you-want-to-go')}</FormHeader>
          <Notifications fullscreen={fullscreen} showBothBookingTypes />
          <BookingSummary values={values} showTime currentView={currentView}/>
        </FormContent>
        <div className={common.contentBottom}>
            <EditFieldsExpirationWarning />
            <FormHeader onlyFullscreen>{departureIsAirport ? t('header-when-does-your-flight-land') : t('header-when-do-you-want-to-go')}</FormHeader>
            <MotionElement.div className={common.content}>
              {showFlightFields ? (
                <div className={common.group}>
                  <div>
                    <p className={common.label}>{t('section-flight-number')}</p>
                    <Field
                      name="flightNumber"
                      placeholder={t('placeholder-flight-number')}
                      component={InputField}
                      disabled={inEditMode && !isFlightNumberEditable}
                      onChange={(e) => {
                        const value = e.target.value || "";
                        setFieldValue('flightNumber', value.toUpperCase());
                      }}
                    />
                  </div>

                  {isTimeEditable && (
                    <div>
                      <p className={common.label}>{t('section-landing-time')}</p>
                      <div className={styles.datepicker}>
                        <Field
                          name="pickupTime"
                          minDate={shouldEnforceConstraints ? minDate : undefined}
                          maxDate={shouldEnforceConstraints ? maxDate : undefined}
                          clampDate={clampDate}
                          component={DatePicker}
                          showTimeInput
                          isAirport={departureIsAirport}
                          onInvalidTime={showInvalidTimeModal}
                        />
                      </div>
                      <ErrorMessage name="pickupTime" component={FormError} />
                    </div>
                  )}

                  {requiresPassengerCount && (
                    <div>
                      <p className={common.label}>{t('header-passengers')}</p>
                      <div className="flex items-center gap-3 py-2">
                        <span className="flex-1 text-[17px] font-medium tracking-[-0.34px] leading-[22px] text-on-surface">{t('label-group-size')}</span>
                        <NumberStepper
                          value={values.passengerCount}
                          min={0}
                          max={maxPassengers}
                          onChange={v => setFieldValue('passengerCount', v)}
                        />
                      </div>
                      <p className={common.subtitle}>{t('info-passenger-count')}</p>
                    </div>
                  )}

                  <div>
                    <p className={common.label}>{t('section-luggage')}</p>
                    <Field
                      name="checkedInBaggage"
                      title={t('title-baggage')}
                      tabIndex="0"
                      alternateBackground={fullscreen}
                      component={CheckboxField}
                      padding="py-2"
                      disabled={inEditMode && !isCheckedBaggageEditable}
                      icon="Luggage"
                    />
                    <p className={common.subtitle}>{t('info-baggage')}</p>
                  </div>
                </div>
              ) : (
                <>
                  {isTimeEditable && (
                    <div>
                      <div className={styles.datepicker}>
                        <Field
                          name="pickupTime"
                          minDate={shouldEnforceConstraints ? minDate : undefined}
                          maxDate={shouldEnforceConstraints ? maxDate : undefined}
                          clampDate={clampDate}
                          component={DatePicker}
                          isAirport={departureIsAirport}
                          onInvalidTime={showInvalidTimeModal}
                        />
                      </div>
                      <ErrorMessage name="pickupTime" component={FormError} />
                    </div>
                  )}

                  {requiresPassengerCount && values.pickupTime !== '' && (
                    <div>
                      <p className={common.label}>{t('header-passengers')}</p>
                      <div className="flex items-center gap-3 py-2">
                        <span className="flex-1 text-[17px] font-medium tracking-[-0.34px] leading-[22px] text-on-surface">{t('label-group-size')}</span>
                        <NumberStepper
                          value={values.passengerCount}
                          min={0}
                          max={maxPassengers}
                          onChange={v => setFieldValue('passengerCount', v)}
                        />
                      </div>
                      <p className={common.subtitle}>{t('info-passenger-count')}</p>
                    </div>
                  )}
                </>
              )}
            </MotionElement.div>

        </div>
        <ButtonDrawer buttons={buttons} fullscreen={fullscreen}/>
      </div>
  )
}

SelectTime.validationSchema = Yup.object({
  pickupTime: Yup.date()
    .when(['bookingId', 'editableFields', 'originalPickupTime', 'availability'], (bookingId, editableFields, originalPickupTime, availability) => {
      const inEditMode = !isNaN(bookingId) && bookingId > 0
      const isTimeEditable = inEditMode ? editableFields?.includes('pickUpTimeStamp') : true

      // Check if time has changed
      const timeHasChanged = originalPickupTime &&
        new Date(originalPickupTime).getTime() !== new Date().getTime() // Will be properly compared during validation

      console.log('[SelectTime] Validation - bookingId:', bookingId, 'inEditMode:', inEditMode, 'isTimeEditable:', isTimeEditable, 'editableFields:', editableFields)

      // Calculate maxDate
      const hasAvailability = availability?.prebook !== undefined && availability?.prebook !== false
      const maxDate = hasAvailability ? getMaxDate({ availability }) : addDays(setHours(setMinutes(setSeconds(new Date(), 0), 59), 23), 365)

      if (inEditMode && !isTimeEditable) {
        // In edit mode with non-editable time: accept any value
        return Yup.date().nullable()
      } else if (inEditMode && isTimeEditable) {
        // In edit mode with editable time: only validate if changed
        // For now, require date but allow past times
        return Yup.date().required(i18next.t('validation-need-date'))
      } else {
        // New booking mode: full validation.
        // Only apply the API-derived minimum when earliestPrebookingDateTime is present.
        // Without it getMinDate falls back to now+40min which would reject valid times
        // during a Fetcher re-fetch when tripRules is momentarily absent.
        const earliest = availability?.prebook?.tripRules?.earliestPrebookingDateTime
        const schema = Yup.date()
          .required(i18next.t('validation-need-date'))
          .max(maxDate, i18next.t('validation-date-too-far-ahead'))
        return earliest
          ? schema.min(getMinDate(availability.prebook.tripRules), i18next.t('validation-date-not-valid'))
          : schema
      }
    }),

  flightNumber: Yup.string()
    .when(['departure', 'bookingId', 'editableFields'], (departure, bookingId, editableFields) => {
      const inEditMode = !isNaN(bookingId) && bookingId > 0
      const isFlightNumberEditable = inEditMode ? editableFields?.includes('originLocation.flightInfo.flightNumber') : true

      if (inEditMode && !isFlightNumberEditable) {
        // In edit mode with non-editable flight number: accept any value
        return Yup.string()
      } else if (departure?.locationInformation?.filter(info => info?.tag === 'FLIGHT_NBR')?.length > 0) {
        // Airport departure requires flight number
        return Yup.string().matches(/^([0-9A-Z][0-9A-Z])[A-Z]?[0-9]{1,4}[A-Z]?$/, i18next.t('validation-need-flight-number')).required(i18next.t('validation-need-flight-number'))
      } else {
        return Yup.string()
      }
    }),

  passengerCount: Yup.number()
    .when(['departure', 'pickupTime'], (departure, pickupTime) => {
      const isPrebook = !!pickupTime
      const required = isPrebook && departure?.locationInformation?.some(info => info?.tag === 'TRAVELER_COUNT_REQUIRED')
      return required
        ? Yup.number().min(1, i18next.t('validation-need-passenger-count'))
        : Yup.number()
    }),

  // Include edit mode fields so they're accessible in .when()
  bookingId: Yup.mixed(),
  editableFields: Yup.mixed(),
  originalPickupTime: Yup.mixed(),
  availability: Yup.mixed(),
})

export default SelectTime
